import React from 'react'
import styled from '@emotion/styled'
import { Link } from 'react-router-dom'
import { Title } from '../../components'

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
`

const Header = styled.div`
  margin-bottom: 40px;
`

const InterviewsSection = styled.div`
  margin-bottom: 40px;
`

const SectionTitle = styled.h2`
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
`

const InterviewList = styled.div`
  display: grid;
  gap: 20px;
`

const InterviewCard = styled.div`
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 20px;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`

const InterviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`

const InterviewTitle = styled.h3`
  font-size: 18px;
  margin: 0;
  color: #333;
`

const InterviewStatus = styled.span<{ status: string }>`
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${({ status }) => {
    if (status === 'completed' || status === 'report_provided') return '#4caf50'
    if (status === 'started' || status === 'in_interview') return '#2196f3'
    if (status === 'scheduled' || status === 'matched') return '#ff9800'
    return '#9e9e9e'
  }};
  color: white;
`

const InterviewInfo = styled.div`
  color: #666;
  font-size: 14px;
  margin-bottom: 8px;
`

const InterviewLink = styled(Link)`
  color: #2196f3;
  text-decoration: none;
  font-weight: 500;
  &:hover {
    text-decoration: underline;
  }
`

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`

const ErrorState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #d32f2f;
`

interface UserInterviewsPageProps {
  regularInterviews: any[]
  avaInterviews: any[]
  isLoading: boolean
  error: string | null
}

const UserInterviewsPage: React.FC<UserInterviewsPageProps> = ({
  regularInterviews,
  avaInterviews,
  isLoading,
  error,
}) => {
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const getInterviewLink = (interview: any, isAva: boolean) => {
    if (isAva) {
      return `/ava/${interview.candidate_token_code}`
    }
    // For regular interviews, we need the interview token
    // This might need adjustment based on your routing
    return `/interviews/${interview.candidate_token_code || interview.id}`
  }

  if (isLoading) {
    return (
      <Container>
        <LoadingState>Loading your interviews...</LoadingState>
      </Container>
    )
  }

  if (error) {
    return (
      <Container>
        <ErrorState>Error: {error}</ErrorState>
      </Container>
    )
  }

  const hasInterviews = regularInterviews.length > 0 || avaInterviews.length > 0

  return (
    <Container>
      <Header>
        <Title>My Interviews</Title>
      </Header>

      {!hasInterviews ? (
        <EmptyState>
          <p>You don't have any interviews yet.</p>
        </EmptyState>
      ) : (
        <>
          {regularInterviews.length > 0 && (
            <InterviewsSection>
              <SectionTitle>Expert Interviews</SectionTitle>
              <InterviewList>
                {regularInterviews.map((interview) => (
                  <InterviewCard key={interview.id}>
                    <InterviewHeader>
                      <InterviewTitle>
                        {interview.desired_position || `Interview #${interview.order_number}`}
                      </InterviewTitle>
                      <InterviewStatus status={interview.status}>
                        {interview.status || 'pending'}
                      </InterviewStatus>
                    </InterviewHeader>
                    {interview.position_company && (
                      <InterviewInfo>Company: {interview.position_company}</InterviewInfo>
                    )}
                    {interview.scheduled_time && (
                      <InterviewInfo>
                        Scheduled: {formatDate(interview.scheduled_time)}
                      </InterviewInfo>
                    )}
                    {interview.created_at && (
                      <InterviewInfo>Created: {formatDate(interview.created_at)}</InterviewInfo>
                    )}
                    {interview.candidate_token_code && (
                      <InterviewLink to={getInterviewLink(interview, false)}>
                        View Interview →
                      </InterviewLink>
                    )}
                  </InterviewCard>
                ))}
              </InterviewList>
            </InterviewsSection>
          )}

          {avaInterviews.length > 0 && (
            <InterviewsSection>
              <SectionTitle>AVA Interviews</SectionTitle>
              <InterviewList>
                {avaInterviews.map((interview) => (
                  <InterviewCard key={interview.id}>
                    <InterviewHeader>
                      <InterviewTitle>
                        {interview.ava_position?.title ||
                          `AVA Interview #${interview.order_number}`}
                      </InterviewTitle>
                      <InterviewStatus status={interview.status}>
                        {interview.status || 'invited'}
                      </InterviewStatus>
                    </InterviewHeader>
                    {interview.ava_position?.company && (
                      <InterviewInfo>Company: {interview.ava_position.company}</InterviewInfo>
                    )}
                    {interview.created_at && (
                      <InterviewInfo>Created: {formatDate(interview.created_at)}</InterviewInfo>
                    )}
                    {interview.candidate_token_code && (
                      <InterviewLink to={getInterviewLink(interview, true)}>
                        View Interview →
                      </InterviewLink>
                    )}
                  </InterviewCard>
                ))}
              </InterviewList>
            </InterviewsSection>
          )}
        </>
      )}
    </Container>
  )
}

export default UserInterviewsPage


